import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { getCMSApiRoute } from "../api.utils";
import { getShowCase } from "../../components/showcase/showcase.utils";

// Cache version for invalidation when data structure changes
const CACHE_VERSION = "1.0";

/**
 * Get cached data from localStorage
 * @param {string} cacheKey - The cache key
 * @returns {Object|null} Cached data or null
 */
const getCachedData = (cacheKey) => {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    
    // Validate cache version
    if (parsed.version !== CACHE_VERSION) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return parsed.data;
  } catch (error) {
    console.warn(`[useShowcaseCache] Error reading cache for ${cacheKey}:`, error);
    return null;
  }
};

/**
 * Save data to localStorage cache
 * @param {string} cacheKey - The cache key
 * @param {Object} data - Data to cache
 */
const setCachedData = (cacheKey, data) => {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
  } catch (error) {
    console.warn(`[useShowcaseCache] Error writing cache for ${cacheKey}:`, error);
  }
};

/**
 * Deep comparison of two objects
 * @param {Object} obj1 
 * @param {Object} obj2 
 * @returns {boolean}
 */
const isDataEqual = (obj1, obj2) => {
  try {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  } catch {
    return false;
  }
};

/**
 * Custom hook for showcase data with stale-while-revalidate caching
 * 
 * @param {Object} options - Hook options
 * @param {string} options.cacheKey - Unique key for localStorage cache
 * @param {string} options.showcaseVersion - Showcase version from config
 * @param {string} options.versionTitle - Version title (e.g., 'film', 'serial')
 * @param {string} options.device - Device type (default: 'web_tv')
 * 
 * @returns {Object} { data, loading, isStale, error, refetch }
 */
export function useShowcaseCache({
  cacheKey,
  showcaseVersion,
  versionTitle,
  device = "web_tv",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isStale, setIsStale] = useState(false);
  const [error, setError] = useState(null);
  const initRef = useRef(false);
  const isMountedRef = useRef(true);

  // Fetch showcase data from API
  const fetchShowcase = useCallback(async () => {
    try {
      const result = await getShowCase(showcaseVersion, versionTitle, device);
      return result;
    } catch (err) {
      console.error(`[useShowcaseCache] Fetch error for ${cacheKey}:`, err);
      throw err;
    }
  }, [showcaseVersion, versionTitle, device, cacheKey]);

  // Main initialization effect
  useEffect(() => {
    // Skip if showcaseVersion is not yet available
    if (!showcaseVersion) {
      return;
    }

    // Only initialize once per showcaseVersion
    if (initRef.current) return;
    initRef.current = true;
    isMountedRef.current = true;

    const initialize = async () => {
      const fullCacheKey = `showcase_${cacheKey}`;
      const cachedData = getCachedData(fullCacheKey);

      if (cachedData) {
        // Cache HIT: Show cached data immediately
        console.log(`[useShowcaseCache] Cache HIT for ${cacheKey}`);
        setData(cachedData);
        setLoading(false);
        setIsStale(true);

        // Silent background refetch
        try {
          const freshData = await fetchShowcase();
          
          if (!isMountedRef.current) return;

          // Only update if data differs
          if (!isDataEqual(cachedData, freshData)) {
            console.log(`[useShowcaseCache] Data updated for ${cacheKey}`);
            setData(freshData);
            setCachedData(fullCacheKey, freshData);
          } else {
            console.log(`[useShowcaseCache] Data unchanged for ${cacheKey}`);
          }
        } catch (err) {
          // Silently fail on background refetch - we still have cached data
          console.warn(`[useShowcaseCache] Background refetch failed for ${cacheKey}:`, err);
        } finally {
          if (isMountedRef.current) {
            setIsStale(false);
          }
        }
      } else {
        // Cache MISS: Fetch fresh data
        console.log(`[useShowcaseCache] Cache MISS for ${cacheKey}`);
        setLoading(true);

        try {
          const freshData = await fetchShowcase();
          
          if (!isMountedRef.current) return;

          setData(freshData);
          setCachedData(fullCacheKey, freshData);
        } catch (err) {
          if (isMountedRef.current) {
            setError(err);
          }
        } finally {
          if (isMountedRef.current) {
            setLoading(false);
          }
        }
      }
    };

    initialize();

    return () => {
      isMountedRef.current = false;
    };
  }, [showcaseVersion, cacheKey, fetchShowcase]);

  // Manual refetch function
  const refetch = useCallback(async () => {
    if (!showcaseVersion) return;
    
    const fullCacheKey = `showcase_${cacheKey}`;
    setIsStale(true);

    try {
      const freshData = await fetchShowcase();
      setData(freshData);
      setCachedData(fullCacheKey, freshData);
    } catch (err) {
      setError(err);
    } finally {
      setIsStale(false);
    }
  }, [showcaseVersion, cacheKey, fetchShowcase]);

  return {
    data,
    loading,
    isStale,
    error,
    refetch,
  };
}

/**
 * Hook that also handles config fetching automatically
 * 
 * @param {Object} options - Hook options
 * @param {string} options.cacheKey - Unique key for localStorage cache
 * @param {string} options.configKey - Key in showcaseConfig (e.g., 'movie', 'series')
 * @param {string} options.versionTitle - Version title (e.g., 'film', 'serial')
 * @param {string} options.device - Device type (default: 'web_tv')
 * 
 * @returns {Object} { data, loading, isStale, error, refetch }
 */
export function useShowcaseCacheWithConfig({
  cacheKey,
  configKey,
  versionTitle,
  device = "web_tv",
}) {
  const [showcaseVersion, setShowcaseVersion] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  const configInitRef = useRef(false);

  // Fetch config first
  useEffect(() => {
    if (configInitRef.current) return;
    configInitRef.current = true;

    const fetchConfig = async () => {
      try {
        const config = await axios.get(getCMSApiRoute("config?device=web_tv"));
        const { data: configData } = config.data;
        setShowcaseVersion(configData.showcaseConfig[configKey]);
      } catch (err) {
        console.error(`[useShowcaseCacheWithConfig] Config error:`, err);
        setConfigError(err);
      } finally {
        setConfigLoading(false);
      }
    };

    fetchConfig();
  }, [configKey]);

  // Use the base hook with the fetched version
  const showcaseResult = useShowcaseCache({
    cacheKey,
    showcaseVersion,
    versionTitle,
    device,
  });

  return {
    ...showcaseResult,
    loading: configLoading || showcaseResult.loading,
    error: configError || showcaseResult.error,
  };
}

export default useShowcaseCacheWithConfig;
